import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useFilterStore, StoreType } from '@/store/filter';
import { STORE_TYPES } from '@/constants'; // STORE_TYPES import

interface FilterOption {
  id: string;
  label: string;
  selected: boolean;
}

// 상수로부터 필터 옵션 초기화
const initialStoreTypes: FilterOption[] = [
  { id: 'all', label: '전체 선택', selected: false },
  ...STORE_TYPES.map(type => ({ ...type, selected: false }))
];

export function ViewFilterPage() {
  const navigate = useNavigate();
  const { selectedStoreTypes, showOpenOnly, showClosedOnly, setSelectedStoreTypes, setShowOpenOnly, setShowClosedOnly } = useFilterStore();
  
  const [storeTypes, setStoreTypes] = useState<FilterOption[]>(initialStoreTypes);

  const [additionalFilters, setAdditionalFilters] = useState<FilterOption[]>([
    { id: 'open_now', label: '주문 가능', selected: false },
    { id: 'closed', label: '주문 불가', selected: false },
  ]);

  // 컴포넌트 마운트 시 저장된 필터 상태 복원
  useEffect(() => {
    setStoreTypes(prev => prev.map(type => ({
      ...type,
      selected: type.id === 'all' 
        ? selectedStoreTypes.length === 0 || selectedStoreTypes.length === prev.length - 1
        : selectedStoreTypes.includes(type.id as StoreType)
    })));

    setAdditionalFilters(prev => prev.map(filter => ({
      ...filter,
      selected: filter.id === 'open_now' ? showOpenOnly : filter.id === 'closed' ? showClosedOnly : false
    })));
  }, [selectedStoreTypes, showOpenOnly, showClosedOnly]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleStoreTypeToggle = (id: string) => {
    if (id === 'all') {
      const newValue = !storeTypes[0].selected;
      setStoreTypes(storeTypes.map(type => ({ ...type, selected: newValue })));
    } else {
      setStoreTypes(storeTypes.map(type => 
        type.id === id ? { ...type, selected: !type.selected } : type
      ));
    }
  };


  const handleAdditionalFilterToggle = (id: string) => {
    setAdditionalFilters(additionalFilters.map(filter => 
      filter.id === id ? { ...filter, selected: !filter.selected } : filter
    ));
  };

  const handleApplyFilter = () => {
    // 선택된 매장 타입 저장
    const selectedTypes = storeTypes
      .filter(type => type.selected && type.id !== 'all')
      .map(type => type.id as StoreType);
    
    setSelectedStoreTypes(selectedTypes);
    
    // 추가 필터 저장
    const openFilter = additionalFilters.find(f => f.id === 'open_now');
    const closedFilter = additionalFilters.find(f => f.id === 'closed');
    
    setShowOpenOnly(openFilter?.selected || false);
    setShowClosedOnly(closedFilter?.selected || false);
    
    navigate(-1);
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header */}
      <header className="flex items-center px-4 py-4 border-b border-gray-200">
        <button onClick={handleBack} className="p-2 -ml-2">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="#222222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="text-lg font-bold text-[#222222] ml-3">필터 설정</h1>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-20">
        {/* 매장 유형 */}
        <section className="mt-6">
          <h2 className="text-base font-semibold text-[#222222] mb-3">매장종류</h2>
          <div className="flex flex-wrap gap-2">
            {storeTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => handleStoreTypeToggle(type.id)}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium transition-colors",
                  type.selected
                    ? "bg-[#86DB89] text-white"
                    : "bg-[#F5F5F5] text-[#666666]"
                )}
              >
                {type.label}
              </button>
            ))}
          </div>
        </section>


        {/* 추가 필터 */}
        <section className="mt-8">
          <h2 className="text-base font-semibold text-[#222222] mb-3">추가 필터</h2>
          <div className="flex flex-wrap gap-2">
            {additionalFilters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => handleAdditionalFilterToggle(filter.id)}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium transition-colors",
                  filter.selected
                    ? "bg-[#86DB89] text-white"
                    : "bg-[#F5F5F5] text-[#666666]"
                )}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </section>
      </div>

      {/* Bottom Button */}
      <div className="px-4 py-4 border-t border-gray-200">
        <button
          onClick={handleApplyFilter}
          className="w-full h-12 bg-[#DEDEDE] rounded-xl flex items-center justify-center text-lg font-semibold text-[#222222] hover:bg-gray-300 transition-colors"
        >
          필터 선택 완료
        </button>
      </div>
    </div>
  );
}